// @ts-strict-ignore
import { useContext, useState } from 'react';
import type { ColumnDef } from '@usealloy/component-library';
import {
  Box,
  Icon,
  Button,
  ButtonSize,
  ButtonVariant,
  IconName,
  Tooltip,
} from '@usealloy/component-library';
import type { MatchData, ResolutionPayload } from '../types';
import StatusBadge from './StatusBadge';
import { UserContext } from '~/dashboard/context';

export const buildAiTooltipText = (match: MatchData) => {
  const reasonText = typeof match?.ai_reason === 'string' ? match.ai_reason.trim() : '';
  const aggregatedCrimes = match?.aggregated_crimes;
  const crimesText = aggregatedCrimes?.length ? `Crimes: ${aggregatedCrimes.join(', ')}` : '';
  const tooltipText = [reasonText, crimesText].filter(Boolean).join('\n\n');
  return tooltipText.length > 0 ? tooltipText : undefined;
};

export type OpenModalOptions = {
  skipPrefill?: boolean;
};

export type AiRecommendationCallbacks = {
  handleResolve: (payload: ResolutionPayload) => void | Promise<void>;
  onOpenModal: (matches: MatchData[], options?: OpenModalOptions) => void;
  enableResolution?: boolean;
};

export const aiRecommendationColumn: ColumnDef<MatchData> = {
  accessorFn: ({ ai_recommendation }) => ai_recommendation,
  cell: ({ getValue, row }) => {
    const value = getValue() as string;
    if (value === null || value === undefined) return '-';
    const tooltipText = buildAiTooltipText(row.original);
    const tooltipContent = tooltipText
      ? <span style={{ whiteSpace: 'pre-line' }}>{tooltipText}</span>
      : undefined;

    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--cl-space-1)' }}>
        <StatusBadge status={value} />
        {tooltipContent && (
          <Tooltip content={tooltipContent} side="top">
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <Icon icon={IconName.Info} size="small" />
            </span>
          </Tooltip>
        )}
      </span>
    );
  },
  enableSorting: false,
  header: 'AI Recommendation',
  id: 'aiRecommendation',
  size: 160,
};

const AiRecommendationCell = ({
  value,
  match,
  handleResolve,
  onOpenModal,
  tooltipContent,
  enableResolution = true,
}: {
  value: string;
  match: MatchData;
  handleResolve: (payload: ResolutionPayload) => void | Promise<void>;
  onOpenModal: (matches: MatchData[], options?: OpenModalOptions) => void;
  tooltipContent?: React.ReactNode;
  enableResolution?: boolean;
}) => {
  const { featureFlags } = useContext(UserContext);
  const watchlistUIIteration = Number(featureFlags?.llmWatchlistUi) || 0;
  const isNeedsReview = match?.status === 'Needs Review' && !match?.is_suppressed;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSubmitting(true);
    const payload: ResolutionPayload = {
      note: typeof match?.ai_reason === 'string' ? match.ai_reason : '',
      is_suppressed: false,
      reason: value,
      service_name: match.service,
      vendor_entity_identifiers: [match.vendor_entity_identifier],
      vendor_search_identifier: match.vendor_search_identifier,
      is_ai_assisted: true,
    };
    try {
      await Promise.resolve(handleResolve(payload));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSubmitting(true);
    try {
      onOpenModal([match], { skipPrefill: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showActionButtons = Boolean(enableResolution) && isNeedsReview && watchlistUIIteration > 0;

  return (
    <Box direction="row" alignItems="center" gap={1}>
      <StatusBadge status={value} />
      {tooltipContent && (
        <Tooltip content={tooltipContent} side="top">
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Icon icon={IconName.Info} size="small" />
          </span>
        </Tooltip>
      )}
      {showActionButtons && (
        <>
          <Box direction="row" gap={1} width="fit-content">
            <Tooltip content="Accept AI recommendation" side="top">
              <Button
                disabled={isSubmitting}
                loading={isSubmitting}
                icon={IconName.Approved}
                iconColorOverride="var(--cl-green-400)"
                variant={ButtonVariant.tertiary}
                style={{ border: '1px solid var(--cl-green-400)', backgroundColor: 'transparent' }}
                onClick={handleAccept}
                size={ButtonSize.xsmall}
                aria-label="Accept AI recommendation"
                data-dd-action-name="alloydd-ai-watchlist-accept-recommendation"
              />
            </Tooltip>
          </Box>
          <Box direction="row" gap={1} width="fit-content">
            <Tooltip content="Reject AI recommendation" side="top">
              <Button
                disabled={isSubmitting}
                icon={IconName.Xmark}
                iconColorOverride="var(--cl-red-400)"
                variant={ButtonVariant.tertiary}
                style={{ border: '1px solid var(--cl-red-400)', backgroundColor: 'transparent' }}
                onClick={handleReject}
                size={ButtonSize.xsmall}
                aria-label="Reject AI recommendation"
                data-dd-action-name="alloydd-ai-watchlist-reject-recommendation"
              />
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );
};

export const createAiRecommendationColumn = ({
  handleResolve,
  onOpenModal,
  enableResolution = true,
}: AiRecommendationCallbacks): ColumnDef<MatchData> => ({
  accessorFn: ({ ai_recommendation }) => ai_recommendation,
  cell: ({ getValue, row }) => {
    const value = getValue() as string;
    const match = row.original;
    if (value === null || value === undefined) return '-';

    const tooltipText = buildAiTooltipText(match);
    const tooltipContent = tooltipText
      ? <span style={{ whiteSpace: 'pre-line' }}>{tooltipText}</span>
      : undefined;

    return (
      <AiRecommendationCell
        value={value}
        match={match}
        handleResolve={handleResolve}
        onOpenModal={onOpenModal}
        tooltipContent={tooltipContent}
        enableResolution={enableResolution}
      />
    );
  },
  enableSorting: false,
  header: 'AI Recommendation',
  id: 'aiRecommendation',
  size: 220,
});

export const aiAssistedColumn: ColumnDef<MatchData> = {
  accessorFn: ({ is_ai_assisted }) => is_ai_assisted,
  cell: ({ getValue, row }) => {
    const value = getValue();
    const status = row?.original?.status;
    const normalizedStatus = typeof status === 'string' ? status.trim().toLowerCase() : null;

    if (value === null || value === undefined) return '-';
    if (normalizedStatus === 'needs review' || normalizedStatus === 'needs_review') return '-';
    return value ? 'Yes' : 'No';
  },
  enableSorting: false,
  header: 'AI Assisted',
  id: 'isAiAssisted',
  size: 110,
};
